import { useRef, useState } from 'react';
import type { Agent } from '../../agents/registry.ts';
import type { SessionSummary } from '../../agents/sessionApi.ts';
import ConfirmDialog from '../ui/ConfirmDialog.tsx';
import SessionContextMenu from './SessionContextMenu.tsx';
import './SessionsPanel.css';

/** Long-press duration (touch) before the context menu opens. */
const LONG_PRESS_MS = 500;

interface SessionsPanelProps {
  agent: Agent;
  collapsed: boolean;
  onToggleCollapse: () => void;
  sessions: readonly SessionSummary[];
  currentSessionId: string;
  loading: boolean;
  error: string | null;
  onNewSession: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => Promise<void>;
  /** Re-attempt the failed history load (shown next to the error message). */
  onRetry: () => void;
}

/** Human "time since" the last turn, for the row meta line. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) {
    return 'now';
  }
  const mins = Math.floor(secs / 60);
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return new Date(iso).toLocaleDateString();
}

interface MenuState {
  sessionId: string;
  x: number;
  y: number;
}

interface ConfirmState {
  sessionId: string;
  title: string;
}

/** Left rail: the agent's real session history plus a pinned agent identity
 *  card. Collapses to a thin rail via the header chevron. */
export default function SessionsPanel({
  agent,
  collapsed,
  onToggleCollapse,
  sessions,
  currentSessionId,
  loading,
  error,
  onNewSession,
  onSelectSession,
  onDeleteSession,
  onRetry,
}: SessionsPanelProps) {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Long-press (touch) plumbing: a pending timer + a flag so the press that
  // opened the menu doesn't also fire the row's click (select).
  const pressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);

  const clearPress = (): void => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const openMenu = (sessionId: string, x: number, y: number): void => {
    setMenu({ sessionId, x, y });
  };

  const onRowPointerDown = (
    e: React.PointerEvent<HTMLButtonElement>,
    sessionId: string,
  ): void => {
    if (e.pointerType !== 'touch') {
      return; // mouse uses right-click (onContextMenu)
    }
    longPressed.current = false;
    clearPress();
    const { clientX, clientY } = e;
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      openMenu(sessionId, clientX, clientY);
    }, LONG_PRESS_MS);
  };

  const onRowClick = (sessionId: string): void => {
    if (longPressed.current) {
      longPressed.current = false;
      return; // this tap opened the menu; don't also select
    }
    onSelectSession(sessionId);
  };

  const askDelete = (sessionId: string): void => {
    const target = sessions.find((s) => s.sessionId === sessionId);
    setMenu(null);
    setDeleteError(null);
    setConfirm({ sessionId, title: target?.title ?? 'this session' });
  };

  const confirmDelete = async (): Promise<void> => {
    if (confirm === null) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteSession(confirm.sessionId);
      setConfirm(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Could not delete that session.',
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <aside className="om-panel om-panel--sessions">
      <div className="om-panel__head om-panel__head--sessions">
        <span className="tick">▸</span>
        <span className="om-panel__head-label">Sessions</span>
        <button
          type="button"
          className="om-collapse-btn"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={collapsed ? 'Expand sessions panel' : 'Collapse sessions panel'}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
            <path
              d="M15 6L9 12L15 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="om-sessions">
        <button type="button" className="om-newsession" onClick={onNewSession}>
          ＋ New session
        </button>

        {error !== null ? (
          <div className="om-sessions__error" role="alert">
            <p className="om-sessions__note om-sessions__note--error">{error}</p>
            <button type="button" className="om-sessions__retry" onClick={onRetry}>
              Retry
            </button>
          </div>
        ) : loading && sessions.length === 0 ? (
          <p className="om-sessions__note">Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <p className="om-sessions__note">
            No past sessions yet. Your conversations will appear here.
          </p>
        ) : (
          sessions.map((session) => (
            <button
              type="button"
              key={session.sessionId}
              className={
                session.sessionId === currentSessionId
                  ? 'om-session is-active'
                  : 'om-session'
              }
              onClick={() => onRowClick(session.sessionId)}
              onContextMenu={(e) => {
                e.preventDefault();
                openMenu(session.sessionId, e.clientX, e.clientY);
              }}
              onPointerDown={(e) => onRowPointerDown(e, session.sessionId)}
              onPointerUp={clearPress}
              onPointerMove={clearPress}
              onPointerLeave={clearPress}
            >
              <span className="om-session__top">
                <span className="om-session__title">{session.title}</span>
              </span>
              <span className="om-session__meta">
                <span>{relativeTime(session.lastTurnAt)}</span>
                <span>·</span>
                <span>
                  {session.turnCount} {session.turnCount === 1 ? 'turn' : 'turns'}
                </span>
              </span>
            </button>
          ))
        )}
      </div>

      <div className="om-agentpick">
        <img className="om-agentpick__img" src={agent.image} alt={agent.name} />
        <div>
          <div className="om-agentpick__name">{agent.name}</div>
          <div className="om-agentpick__role">{agent.role}</div>
        </div>
      </div>

      {menu !== null ? (
        <SessionContextMenu
          x={menu.x}
          y={menu.y}
          onDelete={() => askDelete(menu.sessionId)}
          onClose={() => setMenu(null)}
        />
      ) : null}

      <ConfirmDialog
        open={confirm !== null}
        title="Delete session?"
        message={
          confirm !== null
            ? `“${confirm.title}” and its transcript will be permanently deleted. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleting}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleting) {
            setConfirm(null);
          }
        }}
      />
    </aside>
  );
}
